import React, { useState, useEffect } from 'react';
import '../../styles/components/TemplatesList.scss'
import { createAppTemplate, updateAppTemplate, deleteAppTemplateAll, getAppTemplateDomainById } from '../../api/appTemplatesApi.js';
import { getAppKeywords } from '../../api/appKeyWordsApi.js';
import { useLoader } from '../../context/LoaderContext.js';
import { Box, Grid, Card, CardContent, Typography, Button, Fab } from '@mui/material';
import { Snackbar, Alert } from '@mui/material';
import {
  Add as AddIcon,
  Summarize as IconSummarize,
} from '@mui/icons-material';
import { useDomain } from '../../context/DomainContext.js';
import TemplateModal from './TemplateModal.js';
import DeleteModal from '../DeleteModal.js';
import TemplatesDataGrid from './TemplateDataGrid.js';
import { useAuth } from '../../context/AuthContext.js';

function TemplatesList() {
  const [templates, setTemplates] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateTypeToCreate, setTemplateTypeToCreate] = useState('normal');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, text: '', error: false });
  const { showLoader, hideLoader } = useLoader();
  const { selectedDomain } = useDomain();
  const { user } = useAuth();

  const loadTemplates = () => {
    showLoader();
    getAppTemplateDomainById(selectedDomain)
      .then(response => {
        // Filtrar para mostrar SOLO templates normales (excluir birthday y anniversary)
        const normalTemplates = response.data.filter(
          t => !t.template_type || t.template_type === 'normal'
        );
        setTemplates(normalTemplates);
      })
      .finally(() => { hideLoader() })
      .catch(error => console.error('Error fetching templates:', error));
  };

  const loadKeywords = () => {
    showLoader();
    getAppKeywords()
      .then(response => {
        setKeywords(response.data)
      })
      .finally((data) => { hideLoader() })
      .catch(error => console.error('Error fetching keywords:', error));
  };

  const handleOpenDeleteModal = (template) => {
    setSelectedTemplate(template);
    setOpenDeleteModal(true);
  };

  const handleCloseConfirmModal = () => {
    setOpenDeleteModal(false);
    setSelectedTemplate(null);
  };

  const handleOpenModal = (template, templateType = 'normal') => {
    setTemplateTypeToCreate(templateType);
    setSelectedTemplate(template);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedTemplate(null);
    setTemplateTypeToCreate('normal');
  };

  const handleSaveTemplate = async (data, isNew) => {
    try {
      showLoader();
      let templateMessage = isNew ? 'La firma se creó correctamente' : 'La firma se guardó correctamente';
      isNew ? await createAppTemplate(data) : await updateAppTemplate(selectedTemplate.id, data);

      setOpenModal(false);
      setSelectedTemplate(null);
      setTemplateTypeToCreate('normal');
      setOpenSnackbar({ open: true, text: templateMessage, error: false });
      loadTemplates();
    } catch (error) {
      if (error.type === 'duplicate') {
        setOpenSnackbar({ open: true, text: `Error: ${error.message}`, error: true });
      } else {
        setOpenSnackbar({ open: true, text: `Error: ${error.message}`, error: true });
      }
    } finally {
      hideLoader();
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      showLoader();
      await deleteAppTemplateAll(id);
      setOpenDeleteModal(false);
      setSelectedTemplate(null);
      setOpenSnackbar({ open: true, text: 'La firma se eliminó correctamente', error: true });
      loadTemplates();
    } catch (error) {
      setOpenSnackbar({ open: true, text: 'Hubo un error al eliminar la firma', error: true });
    } finally {
      hideLoader();
    }
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar({ open: false, text: '', error: false });
  };

  useEffect(() => {
    if (selectedDomain) {
      loadTemplates();
      loadKeywords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomain]);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(100vh - 234px)',
      pt: 3,
      pb: 1
    }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="primary" aria-label="add" className='resume-icon'>
                <IconSummarize />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Total
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {templates.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="primary" aria-label="add" className='resume-icon active-templates'>
                <IconSummarize />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Firmas activas
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {Array.isArray(templates) ? templates?.filter(u => u.is_active).length : 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Solo botón para crear firma normal */}
      {user?.isAdmin && (
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 3,
          flexWrap: 'wrap'
        }}>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<AddIcon />} 
            onClick={() => handleOpenModal({}, 'normal')}
          >
            Crear Firma
          </Button>
        </Box>
      )}
      
      <Card className='template-list-datagrid-card' >
        <CardContent>
          <TemplatesDataGrid
            templates={templates}
            handleOpenDeleteModal={handleOpenDeleteModal}
            handleOpenModal={handleOpenModal}
          />
        </CardContent>
      </Card>

      <DeleteModal
        openConfirmModal={openDeleteModal}
        selectedItem={selectedTemplate}
        handleCloseConfirmModal={handleCloseConfirmModal}
        onDelete={handleDeleteTemplate}
        message={'Esta seguro de eliminar firma: ' + selectedTemplate?.template_name}
      />

      <TemplateModal
        openModal={openModal}
        selectedTemplate={selectedTemplate}
        templateTypeToCreate={templateTypeToCreate}
        handleCloseModal={handleCloseModal}
        handleSaveTemplate={handleSaveTemplate}
        keywords={keywords}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        open={openSnackbar.open}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={openSnackbar.error ? 'error' : 'success'}
          variant="filled"
        >
          {openSnackbar.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TemplatesList;